import { useMemo, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { SectionCard } from "@/components/common/SectionCard";
import { usePagination } from "@/components/common/usePagination";
import { ManufacturingGateBanner } from "@/components/manufacturing/ManufacturingGateBanner";
import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useManufacturingEntitlementGate } from "@/components/manufacturing/useManufacturingEntitlementGate";
import {
  DEFAULT_LABOR_RATE_CATALOG_QUERY,
  filterLaborRateCatalog,
  type LaborRateCatalogQuery,
} from "@/lib/manufacturing/filterLaborRateCatalog";
import { LABOR_PAY_DEFAULT_PAGE_SIZE, LABOR_PAY_PAGE_SIZES } from "@/lib/manufacturing/laborPayPaging";
import { LaborBayForm, LaborBaysList } from "./LaborBaysSection";
import { LaborRateCatalogToolbar } from "./LaborRateCatalogToolbar";
import { LaborRateItemForm, LaborRateItemsList } from "./LaborRateItemsSection";
import { useManufacturingLaborBilling } from "./useManufacturingLaborBilling";

export function ManufacturingLaborBillingPanel() {
  const gate = useManufacturingEntitlementGate();
  const [navTick, setNavTick] = useState(0);
  const [catalogQuery, setCatalogQuery] = useState<LaborRateCatalogQuery>(
    DEFAULT_LABOR_RATE_CATALOG_QUERY,
  );
  const state = useManufacturingLaborBilling(gate.enabled, navTick);
  const filteredItems = useMemo(
    () =>
      filterLaborRateCatalog(state.items, {
        ...catalogQuery,
        linksByItem: state.linksByItem,
        bays: state.bays,
      }),
    [catalogQuery, state.bays, state.items, state.linksByItem],
  );
  const page = usePagination(filteredItems, LABOR_PAY_DEFAULT_PAGE_SIZE);

  function updateCatalogQuery(next: LaborRateCatalogQuery) {
    setCatalogQuery(next);
    page.setPageIndex(0);
  }

  return (
    <div className="w-full space-y-4 text-left">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Labor rates</h2>
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            Contractor piece-rate sandbox: author bays, rate items, and which bays earn each line.
            Order sheets and payroll land in later slices.
          </p>
        </div>
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          onClick={() => {
            gate.refresh();
            setNavTick((n) => n + 1);
          }}
          disabled={gate.loading || state.listBusy}
        >
          {state.listBusy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <ManufacturingGateBanner gate={gate} />

      {gate.enabled && state.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {state.localError}
        </div>
      ) : null}

      {gate.enabled && state.seededDefaults ? (
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Seeded five builder bays plus a roofing bay for this org. Edit or add as needed.
        </p>
      ) : null}

      {gate.enabled ? (
        <>
          <SectionCard
            title="Bays"
            description="Org-configurable builder bays and an optional roofing bay that can earn every roof-eligible line."
          >
            <div className="mb-3 flex flex-wrap gap-2">
              <button
                type="button"
                className={ghostBtnClass}
                disabled={state.actionBusy}
                onClick={() => {
                  state.cancelBayForm();
                  state.setShowCreateBay(true);
                }}
              >
                + New bay
              </button>
            </div>
            {state.showCreateBay || state.editingBayId ? (
              <div className="mb-4">
                <LaborBayForm
                  title={state.editingBayId ? "Edit bay" : "Create bay"}
                  draft={state.bayDraft}
                  setDraft={state.setBayDraft}
                  busy={state.actionBusy}
                  onCancel={state.cancelBayForm}
                  onSubmit={() => void state.submitBay()}
                />
              </div>
            ) : null}
            <LaborBaysList
              bays={state.bays}
              busy={state.actionBusy}
              editingBayId={state.editingBayId}
              onEdit={state.startEditBay}
              onToggleActive={(bay) => void state.toggleBayActive(bay)}
              onRemove={(id) => void state.removeBay(id)}
            />
          </SectionCard>

          <SectionCard
            title="Rate catalog"
            description="Free-text labor item codes with flat piece rates. Check which bays earn each item."
          >
            <div className="mb-3 flex flex-wrap gap-2">
              <button
                type="button"
                className={ghostBtnClass}
                disabled={state.actionBusy}
                onClick={() => {
                  state.cancelItemForm();
                  state.setShowCreateItem(true);
                }}
              >
                + New rate item
              </button>
            </div>
            {state.showCreateItem || state.editingItemId ? (
              <div className="mb-4">
                <LaborRateItemForm
                  title={state.editingItemId ? "Edit rate item" : "Create rate item"}
                  draft={state.itemDraft}
                  setDraft={state.setItemDraft}
                  busy={state.actionBusy}
                  onCancel={state.cancelItemForm}
                  onSubmit={() => void state.submitItem()}
                />
              </div>
            ) : null}
            {state.items.length > 0 ? (
              <LaborRateCatalogToolbar
                query={catalogQuery}
                bays={state.bays}
                onChange={updateCatalogQuery}
                onClear={() => updateCatalogQuery(DEFAULT_LABOR_RATE_CATALOG_QUERY)}
              />
            ) : null}
            {state.listBusy && state.items.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-zinc-400">Loading…</p>
            ) : filteredItems.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-zinc-400">
                {state.items.length === 0
                  ? "No rate items yet. Add free-text labor codes with a flat piece rate."
                  : "No rate items match that search or filter."}
              </p>
            ) : (
              <LaborRateItemsList
                items={page.paginatedItems}
                bays={state.bays}
                linksByItem={state.linksByItem}
                busy={state.actionBusy}
                editingItemId={state.editingItemId}
                onEdit={state.startEditItem}
                onToggleActive={(item) => void state.toggleItemActive(item)}
                onRemove={(id) => void state.removeItem(id)}
                onSetBayLinks={(itemId, bayIds) => void state.setItemBayLinks(itemId, bayIds)}
              />
            )}
            <div className="mt-3">
              <PaginationBar
                pageIndex={page.pageIndex}
                pageCount={page.pageCount}
                pageSize={page.pageSize}
                totalCount={page.totalCount}
                firstShown={page.firstShown}
                lastShown={page.lastShown}
                onPageChange={page.setPageIndex}
                onPageSizeChange={page.setPageSize}
                pageSizeOptions={LABOR_PAY_PAGE_SIZES}
                noun="rates"
                alwaysShow={filteredItems.length > 0}
              />
            </div>
          </SectionCard>
        </>
      ) : null}
    </div>
  );
}
